import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { campaignService } from '../services/campaignService';

export const Dashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const { currentUser } = useAuth();
  const [isMobile, setIsMobile] = useState(false);

  const campaignsPerPage = 6;
  const totalPages = Math.ceil(campaigns.length / campaignsPerPage);
  const startIndex = currentPage * campaignsPerPage;
  const endIndex = startIndex + campaignsPerPage;
  const currentCampaigns = campaigns.slice(startIndex, endIndex);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const handleMatch = event => setIsMobile(event.matches);
    handleMatch(media);
    media.addEventListener('change', handleMatch);
    return () => media.removeEventListener('change', handleMatch);
  }, []);

  const mobileSlides = useMemo(() => {
    if (!isMobile) return [];
    const slides = [];
    const source = campaigns;
    for (let i = 0; i < source.length; i += 3) {
      slides.push(source.slice(i, i + 3));
    }
    return slides;
  }, [isMobile, campaigns]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setIsLoading(true);
        // For unauthenticated users, use public campaigns endpoint
        const response = currentUser 
          ? await campaignService.getCampaigns()
          : await campaignService.getPublicCampaigns();
        
        // The API returns the campaigns directly as an array
        if (Array.isArray(response)) {
          setCampaigns(response);
        } else {
          setError('Invalid response format');
        }
      } catch (err) {
        setError('An error occurred while fetching campaigns');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, [currentUser]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-brown-600" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-brown-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-shell">
      <div className="dashboard-header dashboard-header--centered">
        <div className="dashboard-header__content">
          <h1 className="dashboard-title">Latest Campaigns</h1>
        </div>
        {currentUser && (
          <Link
            to="/create-campaign"
            className="dashboard-cta dashboard-cta--centered"
          >
            Create Campaign
          </Link>
        )}
      </div>

      {campaigns.length === 0 ? (
        <div className="dashboard-empty">
          <h3>No campaigns found</h3>
          <p>Be the first to create a campaign!</p>
        </div>
      ) : (
        <>
          {isMobile ? (
            <>
              <div className="dashboard-slider" aria-label="Latest campaigns">
                {mobileSlides.map((slide, index) => (
                  <div key={`slide-${index}`} className="dashboard-slide">
                    {slide.map(campaign => (
                      <div key={campaign.id} className="campaign-card">
                        <div className="campaign-card__header">
                          <div className="campaign-card__badge">
                            <svg className="campaign-card__badge-icon" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-9-3a2 2 0 114 0 2 2 0 01-4 0zm-2 5a5 5 0 019.584 1.249A7.002 7.002 0 0110 17a7.002 7.002 0 01-6.584-3.751A5 5 0 017 12z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="campaign-card__title">{campaign.title}</h3>
                            <p className="campaign-card__subtitle">Campaign</p>
                          </div>
                        </div>

                        <div className="campaign-card__meta">
                          <div className="campaign-card__chip">
                            <svg viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>{formatDate(campaign.createdAt)}</span>
                          </div>
                          <div className="campaign-card__chip">
                            <svg viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>Campaign Owner</span>
                          </div>
                        </div>

                        <Link to={`/campaigns/${campaign.id}`} className="campaign-card__button">
                          View Details
                        </Link>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="dashboard-slider__hint" aria-hidden="true">
                Swipe to explore →
              </div>
            </>
          ) : (
            <div className="dashboard-grid">
              {currentCampaigns.map(campaign => (
                <div key={campaign.id} className="campaign-card">
                  <div className="campaign-card__header">
                    <div className="campaign-card__badge">
                      <svg className="campaign-card__badge-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-9-3a2 2 0 114 0 2 2 0 01-4 0zm-2 5a5 5 0 019.584 1.249A7.002 7.002 0 0110 17a7.002 7.002 0 01-6.584-3.751A5 5 0 017 12z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="campaign-card__title">{campaign.title}</h3>
                      <p className="campaign-card__subtitle">Campaign</p>
                    </div>
                  </div>

                  <div className="campaign-card__meta">
                    <div className="campaign-card__chip">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{formatDate(campaign.createdAt)}</span>
                    </div>
                    <div className="campaign-card__chip">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Campaign Owner</span>
                    </div>
                  </div>

                  <Link to={`/campaigns/${campaign.id}`} className="campaign-card__button">
                    View Details
                  </Link>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && !isMobile && (
            <div className="dashboard-pagination">
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className="dashboard-pagination__button"
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <span className="dashboard-pagination__status">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages - 1}
                className="dashboard-pagination__button"
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
